import {
  cloneElement,
  Fragment,
  isValidElement,
  memo,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Popover, type PopoverProps } from "antd";
import { useTracking } from "react-tracking";

import { IconInfoCircle } from "@btnet/blueprint-icons";
import { cn } from "@btnet/utilities-core";

import "./BTPopover.less";
import {
  addCloseOnScroll,
  getClosestModal,
  removeCloseOnScroll,
} from "./BTPopover.utility";

interface IBTPopoverTrackingData {
  component?: string;
  uniqueId?: string;
  event?: string;
}

export type TBTPopoverTrigger = "hover" | "focus" | "click" | "contextMenu";

export type BTPopoverPlacement =
  | "top"
  | "topLeft"
  | "topRight"
  | "bottom"
  | "bottomLeft"
  | "bottomRight"
  | "left"
  | "leftTop"
  | "leftBottom"
  | "right"
  | "rightTop"
  | "rightBottom";

export interface IBTPopoverProps extends PopoverProps {
  /**
   * Position of the popover relative to the trigger
   * @default "top"
   */
  placement?: BTPopoverPlacement;

  /**
   * Set width type for popover; "medium" was BdsTooltip's default
   * @default narrow
   */
  widthType?: "narrow" | "medium" | "wide" | "unrestricted";

  /**
   * Remove automatic margin from popover component. Spacing will also be removed if
   * popover children elements are provided
   */
  removeSpacing?: boolean;

  /**
   * Use "click" to only show content on click instead of hover.
   * - A hover trigger will also add a Focus trigger for a11y.
   * @default hover
   */
  trigger?: TBTPopoverTrigger | TBTPopoverTrigger[];

  /**
   * Adds word-break: break-word to the inner content
   * @default true
   */
  breakWord?: boolean;

  /**
   * Suppresses the popover while still rendering children as normal.
   * Prefer this over conditional rendering (`{condition && <BTPopover>}`) —
   * it avoids mount/unmount cycles and keeps the component tree stable.
   * @default false
   */
  disabled?: boolean;

  /**
   * Add the scroll event
   * @default true
   */
  shouldCloseOnScroll?: boolean;

  /**
   * Removes the default border and padding from the popover inner content
   * @default false
   */
  borderless?: boolean;

  /**
   * Prevents touch and click events on the trigger from propagating to parent
   * elements. Useful when the popover is inside an interactive container (e.g.,
   * a sortable grid header) where event bubbling would cause side effects.
   * @default false
   */
  stopPropagation?: boolean;

  /**
   * Used for analytics tracking only. This value is **not** rendered to the DOM —
   * antd's Popover does not forward unknown props to its overlay or trigger wrapper.
   * To make a trigger element queryable in tests, add `data-testid` directly to the
   * child element passed to BTPopover.
   */
  "data-testid"?: string;
}

/**
 * Returns identifier that should be used in analytics tracking.
 * NOTE: We have limited info around these popovers, so this attempts to use what
 * relevant data we have in order to supply some context around the event.
 * @param props
 * @returns string for tracking uniqueId
 */
const getUniqueIdForTracking = (props: IBTPopoverProps) => {
  if (props["data-testid"]) {
    return props["data-testid"];
  } else if (typeof props.title === "string") {
    return props.title;
  } else if (typeof props.content === "string") {
    return props.content;
  } else {
    return "Popover";
  }
};

/**
 * Whether a `content`/`title` value is something React would actually render. Blank/whitespace
 * strings, `null`, `undefined`, and the booleans `true`/`false` all render nothing — the last
 * matters because the `{condition && <JSX/>}` idiom yields `false` when the condition is unmet,
 * which must count as "nothing to show" so no default icon anchors to it. Numbers (including `0`)
 * and other ReactNodes are renderable.
 */
const hasRenderableValue = (value: ReactNode): boolean =>
  typeof value === "string"
    ? value.trim().length > 0
    : value !== null && value !== undefined && typeof value !== "boolean";

/**
 * Resolves the trigger element for the popover and injects `aria-describedby`.
 *
 * - If children are a string or fragment, wraps them in a `<span>` so aria attributes can be attached.
 * - If no children are provided, renders a default info icon button only when there is content to
 *   show and the popover is not suppressed. The default icon exists to give blank content something
 *   to anchor to, so it must never appear for blank content (regardless of controlled/uncontrolled).
 * - Returns `null` when there is nothing to render (including the suppressed + no-children case).
 * - Otherwise, clones the element to inject `aria-describedby` linking to the popover content.
 * - When suppressed (disabled, or blank content in uncontrolled mode), no `aria-describedby`
 *   is attached and (for the no-children case) no default icon is rendered.
 *
 * @param children - The trigger element passed to BTPopover
 * @param hasContent - Whether the popover has non-blank content to display
 * @param isSuppressed - Whether the popover is suppressed (disabled or blank uncontrolled content)
 * @param popoverId - The id of the popover, used for `aria-describedby`
 * @param isVisible - Whether the popover is currently visible
 * @returns The resolved trigger element with aria attributes, or `null`
 */
const getTriggerContent = (
  children: ReactNode,
  hasContent: boolean,
  isSuppressed: boolean,
  popoverId: string,
  isVisible: boolean,
): ReactNode => {
  if (children === null || children === undefined || children === false) {
    if (isSuppressed || !hasContent) {
      return null;
    }
    return (
      <span
        data-testid="popover-icon"
        className="PopoverIconButton"
        role="button"
        tabIndex={0}
        aria-label="Info"
        aria-describedby={isVisible ? popoverId : undefined}
        // Prevent ancestor <label> elements from stealing focus to their
        // associated input when the popover trigger is tapped. Without this,
        // popovers inside FormItem labels flash open then immediately close on
        // mobile because the label's default click behavior moves focus away
        // from the trigger.
        onClick={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
          }
        }}
      >
        <IconInfoCircle />
      </span>
    );
  }

  if (
    !isSuppressed &&
    isValidElement(children) &&
    (children as ReactElement).type !== Fragment
  ) {
    return cloneElement(children as ReactElement, {
      "aria-describedby": isVisible ? popoverId : undefined,
    });
  }

  return children;
};

/**
 * Popover component that displays floating content anchored to a trigger element.
 * Built on top of antd Popover with BDS styling and sensible defaults.
 *
 * When no children are provided, renders a default info circle icon as the trigger.
 * Supports both controlled (`visible` prop) and uncontrolled modes.
 * Automatically closes on scroll and tracks open events for analytics.
 *
 * @param placement - Position relative to trigger. Defaults to `"top"`.
 * @param trigger - Interaction that opens the popover. Defaults to `"hover"`.
 * @param breakWord - Applies `word-break: break-word` to content. Defaults to `true`.
 * @param disabled - Prevents the popover from opening and renders only children. Defaults to `false`.
 * @param shouldCloseOnScroll - Closes the popover when the page scrolls. Defaults to `true`.
 * @param widthType - Controls max-width of the overlay: `"narrow"`, `"medium"`, `"wide"`, or `"unrestricted"`. Defaults to `"narrow"`.
 * @param visible - Controlled visibility state. When provided, the component syncs to this value.
 * @param onVisibleChange - Callback fired when visibility changes.
 */
export const BTPopover = memo(function BTPopover({
  placement = "top",
  className,
  overlayClassName,
  children,
  removeSpacing: _removeSpacing,
  trigger = "hover",
  breakWord = true,
  disabled = false,
  shouldCloseOnScroll = true,
  widthType = "narrow",
  stopPropagation = false,
  onVisibleChange,
  visible: visibleProp,
  "data-testid": dataTestId,
  ...otherProps
}: IBTPopoverProps) {
  const { trackEvent } = useTracking<IBTPopoverTrackingData>({
    component: "Popover",
    uniqueId: getUniqueIdForTracking({
      "data-testid": dataTestId,
      ...otherProps,
    } as IBTPopoverProps),
  });

  const popoverId = useMemo(
    () => otherProps.id ?? crypto.randomUUID(),
    [otherProps.id],
  );

  // Blank content (empty string, whitespace, null, or undefined) has nothing to show,
  // so it must not mount an empty antd overlay. ReactNode content (icons/JSX) is always
  // considered present.
  // antd renders its overlay for either `title` or `content`, so both count toward
  // "has something to show".
  const hasContent =
    hasRenderableValue(otherProps.content) ||
    hasRenderableValue(otherProps.title);

  // Ensure hover triggers are also keyboard-accessible via focus
  const normalizedTrigger = useMemo(() => {
    if (trigger === "hover") {
      return ["hover", "focus"] as TBTPopoverTrigger[];
    }
    if (
      Array.isArray(trigger) &&
      trigger.includes("hover") &&
      !trigger.includes("focus")
    ) {
      return [...trigger, "focus"] as TBTPopoverTrigger[];
    }
    return trigger;
  }, [trigger]);

  const [internalVisible, setInternalVisible] = useState(false);
  const refPopover = useRef<{ getRootDomNode: () => HTMLElement }>(null);

  // In controlled mode (visibleProp provided), use the prop synchronously
  // so the caller can gate visibility (e.g. TextTruncate only shows on overflow).
  // In uncontrolled mode, use internal state.
  const isControlled = visibleProp !== undefined;

  // Blank content has nothing to show, so it suppresses the popover the same way
  // `disabled` does, without changing the meaning of the `disabled` prop itself.
  // Only applies in uncontrolled mode: a controlled caller owns visibility and may
  // render content lazily (null while closed, populated once it opens), so blank
  // content must not block an explicit `visible` prop.
  const isDisabled = disabled || (!isControlled && !hasContent);

  const visible = !isDisabled && (isControlled ? visibleProp : internalVisible);
  const setVisible = setInternalVisible;

  const handleScroll = useCallback(() => {
    setVisible(false);
    onVisibleChange?.(false);

    if (
      Array.isArray(normalizedTrigger) &&
      normalizedTrigger.includes("focus")
    ) {
      (document.activeElement as HTMLElement)?.blur();
    }
  }, [normalizedTrigger, onVisibleChange, setVisible]);

  const stopPropagationHandlers = stopPropagation
    ? {
        onClick: (e: React.MouseEvent) => e.stopPropagation(),
        onTouchStart: (e: React.TouchEvent) => e.stopPropagation(),
        onTouchEnd: (e: React.TouchEvent) => e.stopPropagation(),
      }
    : undefined;

  // Add/remove scroll-to-close listener when visibility changes
  useEffect(() => {
    const triggerEl = refPopover.current?.getRootDomNode();

    if (visible && triggerEl?.closest && shouldCloseOnScroll) {
      addCloseOnScroll(triggerEl, handleScroll, true);
    }

    return () => {
      if (triggerEl) {
        removeCloseOnScroll(triggerEl, handleScroll, true);
      }
    };
  }, [visible, shouldCloseOnScroll, handleScroll]);

  const handleVisibleChange = useCallback(
    (isVisible: boolean) => {
      setVisible(isVisible);

      const triggerEl = refPopover.current?.getRootDomNode();
      if (!isVisible && triggerEl) {
        removeCloseOnScroll(triggerEl, handleScroll, true);
      }

      if (!isDisabled) {
        onVisibleChange?.(isVisible);

        if (isVisible) {
          trackEvent({ event: "PopoverOpen" });
        }
      }
    },
    [isDisabled, onVisibleChange, trackEvent, handleScroll, setVisible],
  );

  const triggerContent = getTriggerContent(
    children,
    hasContent,
    isDisabled,
    popoverId,
    visible,
  );
  if (triggerContent === null) {
    return null;
  }

  const popoverClassNames = cn("BTPopover", className, {
    PopoverEnabled: !isDisabled,
  });

  const overlayClassNames = cn("BTPopoverOverlay", {
    [overlayClassName!]: !!overlayClassName,
    PopoverBreak: breakWord,
    narrow: widthType === "narrow",
    medium: widthType === "medium",
    wide: widthType === "wide",
    borderless: otherProps.borderless,
  });

  return (
    <Popover
      ref={refPopover}
      getPopupContainer={getClosestModal}
      autoAdjustOverflow
      id={popoverId}
      className={popoverClassNames}
      overlayClassName={overlayClassNames}
      placement={placement}
      trigger={normalizedTrigger}
      visible={visible}
      {...otherProps}
      {...stopPropagationHandlers}
      onVisibleChange={handleVisibleChange}
    >
      <span data-testid={isDisabled ? undefined : dataTestId}>
        {triggerContent}
      </span>
    </Popover>
  );
});

/**
 * Hack to close active popovers by triggering a touchstart & mousedown event on the page
 * @see https://github.com/ant-design/ant-design/issues/28464
 */
export function hack_closePopovers(element: HTMLElement = document.body) {
  const touchStartEvent = new Event("touchstart", {
    bubbles: true,
  });

  // Native Event has no .touches/.targetTouches/.changedTouches — only TouchEvent does.
  // Some libraries (e.g. rc-drawer's removeStartHandler) read e.touches[0].clientX
  // unconditionally on touchstart and crash when these are undefined. Stub a single
  // zeroed Touch so third-party touchstart listeners see a valid no-op touch.
  const fakeTouch = { clientX: 0, clientY: 0 } as Touch;
  const touchList = [fakeTouch];
  Object.defineProperty(touchStartEvent, "touches", { value: touchList });
  Object.defineProperty(touchStartEvent, "targetTouches", { value: touchList });
  Object.defineProperty(touchStartEvent, "changedTouches", {
    value: touchList,
  });

  const mouseDownEvent = new Event("mousedown", {
    bubbles: true,
  });

  element.dispatchEvent(touchStartEvent);
  element.dispatchEvent(mouseDownEvent);
}
